import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

import CategoryBox from './CategoryBox'
import FiltersControl from './FiltersControl'
import ViewOnlyBox from './ViewOnlyBox'

import { useDialog } from '_hooks'
import IconFilter from '_icons/IconFilter'

import { AC_FILTERS, TOP_LEVEL_FILTERS, DEFAULT_PERCENTILE } from './constants'
import PREDICTORS_VALUES from './static/predictors_values.js'

import './styles.scss'

const updateCheck = (data, cat, item) => {
  const copy = JSON.parse(JSON.stringify(data))
  const category = copy.find(({ category }) => category.name === cat.category.name)
  const currentValue = category.values.find((value) => value.name === item.name)
  currentValue.check = !currentValue.check
  return copy
}

const clearCheck = (data, catName) => {
  const copy = JSON.parse(JSON.stringify(data))
  const category = copy.find(({ category }) => category.name === catName)
  category.values = category.values.map((value) => ({ ...value, check: false }))
  return copy
}

const updatePercentile = (data, cat, item, topValue) => {
  const copy = JSON.parse(JSON.stringify(data))
  const category = copy.find(({ category }) => category.name === cat.category.name)
  const currentValue = category.values.find((value) => value.name === item.name)
  const clampedTop = Math.max(0, Math.min(100, Number(topValue) || 0))
  // store percentile as inverse of "Top x %"
  currentValue.percentile = 100 - clampedTop
  return copy
}

const Filters = ({
  data,
  name = 'Filters',
  onChange = (data) => {},
  disabled,
  customAttributes,
}) => {
  const [topLevel, setTopLevel] = useState(data[0].topLevel)
  const [selectedCategory, setSelectedCategory] = useState(data[0].category.name)
  const [openTarget, setOpenTarget] = useState(false)

  const baseRef = useRef(null)
  const targetsRef = useRef({})

  const { dialog, Dialog } = useDialog({
    wrapperNode: baseRef.current,
    targetNode: targetsRef.current[openTarget],
    maxSize: {
      width: 750,
      height: 320,
    },
  })

  const handleDialog = (target) => () => {
    setOpenTarget(target)
    if (target !== 'initial') {
      setSelectedCategory(target)
    }
    dialog.show()
  }

  const handleCheckboxChange = (cat, item) => () => {
    if (disabled) return
    onChange(updateCheck(data, cat, item))
  }

  const handlePercentileChange = (cat, item) => (event) => {
    if (disabled) return
    onChange(updatePercentile(data, cat, item, event.target.value))
  }

  const handleClearCat = (catName) => () => {
    if (disabled) return
    onChange(clearCheck(data, catName))
  }

  const categoriesWithValues = data.filter((category) =>
    category.values.some((value) => value.check)
  )

  return (
    // TODO: implement disabled class
    <div className={classNames('filters', { 'filters--disabled': disabled })} ref={baseRef}>
      <button
        className="filters__initial"
        ref={(el) => (targetsRef.current['initial'] = el)}
        onClick={handleDialog('initial')}
      >
        <IconFilter fill="#fff" />
        <span>{name}</span>
      </button>
      <div className="filters__selected">
        {categoriesWithValues.map((item) => (
          <CategoryBox
            key={item.category.name}
            item={item}
            handleClearCat={handleClearCat}
            targetsRef={targetsRef}
            handleDialog={handleDialog}
          />
        ))}
        <ViewOnlyBox label="Custom Attributes" item={customAttributes} />
      </div>
      <Dialog>
        <FiltersControl
          data={data}
          openTarget={openTarget}
          topLevel={topLevel}
          setTopLevel={setTopLevel}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          handleCheckboxChange={handleCheckboxChange}
          handlePercentileChange={handlePercentileChange}
        />
      </Dialog>
    </div>
  )
}

const connect = {
  in: (audience, asyncData) => {
    // Base filters from constants (include topLevel for UI grouping)
    const base = AC_FILTERS.map(({ topLevel, category, values, getValues }) => ({
      topLevel,
      category,
      values:
        getValues?.(audience, asyncData) ??
        values.map((value) => ({
          ...value,
          check: audience?.[category.name]?.some?.(
            (item) => item.toLowerCase() === value.name.toLowerCase()
          ),
        })),
    }))

    // Predictors (UI groups) <-> predictors (API flat array)
    const predictorsAudience = audience?.predictors || []
    const predictorsLookup = (predictorsAudience || []).reduce((acc, item) => {
      const key = item?.value?.toLowerCase?.()
      if (key) acc[key] = item
      return acc
    }, {})
    // Group static predictors by top-level path and include the top-level item
    const getTopLevel = (path) => {
      const parts = (path || '').split('/').filter(Boolean)
      return parts[0] ? `/${parts[0]}` : ''
    }
    const groups = PREDICTORS_VALUES.reduce((acc, path) => {
      const top = getTopLevel(path)
      if (!top) return acc
      acc[top] = acc[top] || new Set()
      acc[top].add(path)
      return acc
    }, {})
    const predictors = Object.entries(groups).map(([groupKey, set]) => {
      const values = Array.from(set).sort((a, b) => a.localeCompare(b))
      return {
        topLevel: TOP_LEVEL_FILTERS.predictors,
        category: {
          label: groupKey,
          name: `predictor_${groupKey.replace(/\W+/g, '_')}`,
        },
        values: values.map((valuePath) => {
          const selected = predictorsLookup[valuePath.toLowerCase()]
          return {
            label: valuePath,
            name: valuePath,
            check: !!selected,
            percentile: selected?.percentile,
          }
        }),
      }
    })

    return [...base, ...predictors]
  },
  out: (options) => {
    const output = {}
    const predictorsOut = []

    options.forEach(({ category, values }) => {
      if (category.name.startsWith('predictor_')) {
        const selected = values
          .filter((v) => v.check)
          .map((v) => ({
            value: v.saveValue ?? v.name,
            percentile: Number.isFinite(v.percentile) ? v.percentile : DEFAULT_PERCENTILE,
          }))
        if (selected.length) {
          predictorsOut.push(...selected)
        }
      } else {
        output[category.name] = values
          .map((value) => (value.check ? value.saveValue ?? value.name : null))
          .filter(Boolean)
      }
    })

    if (predictorsOut.length) {
      output.predictors = predictorsOut
    }

    return output
  },
}

const GeneralFilters = ({ audience, setAudience, disabled, asyncData }) => {
  const [data, setData] = useState(connect.in(audience, asyncData))
  const customAttributes = audience?.customAttributes ?? []

  const handleChange = (newData) => {
    const output = connect.out(newData)
    // Ensure predictors is cleared when no predictors are selected
    if (!Object.prototype.hasOwnProperty.call(output, 'predictors')) {
      output.predictors = []
    }
    setAudience((audience) => ({
      ...audience,
      ...output,
    }))
  }

  useEffect(() => {
    setData(connect.in(audience, asyncData))
  }, [audience, asyncData])

  return (
    <Filters
      data={data.filter(({ values }) => !!values.length)}
      customAttributes={customAttributes}
      onChange={handleChange}
      disabled={disabled}
    />
  )
}

export default GeneralFilters
